import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { Suspense, useEffect } from "react";
import { getBlogPost, getAlternatePost, getRelatedPosts as getStaticRelated, CONTENT_MAP, type BlogPost as StaticBlogPost } from "~/content/blog";
import { buildMeta } from "~/lib/seo/meta";
import { articleSchema, breadcrumbSchema, faqPageSchema, schemaGraph, supplementalBlogSchemaNodes } from "~/lib/seo/schema";
import { JsonLd } from "~/components/seo/JsonLd";
import { trackEvent } from "~/lib/posthog-client";
import { useBlogEngagement } from "~/lib/hooks/useBlogEngagement";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { getLocale } from "~/paraglide/runtime";
import { BlogPostLink, LocalizedBlogLink } from "~/components/blog/BlogLink";
import { getBlogCanonical, getBlogHeadLinks } from "~/lib/blog-seo";
import { asAnalyticsLocale, asBlogPageType } from "~/lib/analytics-events";

// ── Types ───────────────────────────────────────────────────────────────────

interface LoaderData {
  source: "db" | "static";
  slug: string;
  locale: string;
  title: string;
  excerpt: string | null;
  content: string | null;
  author: string | null;
  readingTimeMin: number | null;
  publishedAt: string | null;
  updatedAt: string | null;
  tags: string[];
  pageType: string;
  metaTitle: string | null;
  metaDescription: string | null;
  ogImageUrl: string | null;
  schemaJson: Record<string, any> | null; // any required for TanStack Start serialization
  countryCode: string | null;
  city: string | null;
  roleCategory: string | null;
  pillar: { slug: string; title: string } | null;
  internalLinks: Array<{
    anchorText: string | null;
    linkType: string;
    toSlug: string;
    toTitle: string;
    toLocale: string;
    toPageType: "country" | "city" | "role" | "visa" | "university" | "editorial";
    toCountryCode: string | null;
  }>;
  related: Array<{
    slug: string;
    locale: string;
    pageType: "country" | "city" | "role" | "visa" | "university" | "editorial";
    countryCode: string | null;
    title: string;
    excerpt: string | null;
    readingTimeMin: number | null;
    publishedAt: string | null;
  }>;
  alternate: {
    slug: string;
    locale: string;
    pageType: "country" | "city" | "role" | "visa" | "university" | "editorial";
    countryCode: string | null;
  } | null;
  /** Visible-FAQ pairs for static posts; emitted as FAQPage JSON-LD. DB posts leave this null. */
  faq: Array<{ question: string; answer: string }> | null;
}

// ── Server data loader ──────────────────────────────────────────────────────

const fetchBlogPost = createServerFn({ method: "GET" })
  .validator(z.string())
  .handler(async ({ data: slug }) => {
    const { getPostBySlug, getRelatedPosts } = await import("~/lib/blog-queries");
    const requestedLocale = getLocale();

    // Try DB first
    let post: Awaited<ReturnType<typeof getPostBySlug>> = null;
    try {
      post = await getPostBySlug(slug, requestedLocale);
    } catch (err) {
      console.error("[blog] getPostBySlug failed:", slug, err instanceof Error ? err.message : err);
    }

    // If DB post exists but has no content, check if a static TSX version exists.
    // During migration, posts are seeded with content: null while static TSX files
    // still serve the body. Fall through to the static path in that case.
    const hasStaticVersion = getBlogPost(slug)?.locale === requestedLocale;
    if (post && !post.content && hasStaticVersion) {
      // Fall through to static path below
    } else if (post) {
      let related: Awaited<ReturnType<typeof getRelatedPosts>> = [];
      try {
        related = await getRelatedPosts(post.id, post.tags, post.locale, 3);
      } catch (err) {
        console.error("[blog] getRelatedPosts failed:", err instanceof Error ? err.message : err);
      }

      const data: LoaderData = {
        source: "db",
        slug: post.slug,
        locale: post.locale,
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        author: post.author,
        readingTimeMin: post.readingTimeMin,
        publishedAt: post.publishedAt?.toISOString() ?? null,
        updatedAt: post.updatedAt.toISOString(),
        tags: post.tags,
        pageType: post.pageType,
        metaTitle: post.metaTitle,
        metaDescription: post.metaDescription,
        ogImageUrl: post.ogImageUrl,
        schemaJson: post.schemaJson,
        countryCode: post.countryCode,
        city: post.city,
        roleCategory: post.roleCategory,
        pillar: post.pillar,
        internalLinks: post.internalLinks,
        related: related.map((r) => ({
          slug: r.slug,
          locale: r.locale,
          pageType: r.pageType,
          countryCode: r.countryCode,
          title: r.title,
          excerpt: r.excerpt,
          readingTimeMin: r.readingTimeMin,
          publishedAt: r.publishedAt?.toISOString() ?? null,
        })),
        alternate: post.alternate,
        faq: null,
      };
      return data;
    }

    // Fallback: static content
    const staticPost = getBlogPost(slug);
    if (!staticPost || staticPost.locale !== requestedLocale) return null;

    const staticRelated = getStaticRelated(slug);
    const alt = getAlternatePost(slug);

    const data: LoaderData = {
      source: "static",
      slug: staticPost.slug,
      locale: staticPost.locale,
      title: staticPost.title,
      excerpt: staticPost.description,
      content: null,
      author: staticPost.author,
      readingTimeMin: staticPost.readingTimeMin,
      publishedAt: staticPost.publishedAt,
      updatedAt: staticPost.updatedAt ?? null,
      tags: staticPost.tags,
      pageType: "editorial",
      metaTitle: null,
      metaDescription: staticPost.description,
      ogImageUrl: staticPost.coverImage ?? null,
      schemaJson: null,
      countryCode: null,
      city: null,
      roleCategory: null,
      pillar: null,
      internalLinks: [],
      related: staticRelated.map((r) => ({
        slug: r.slug,
        locale: r.locale,
        pageType: "editorial",
        countryCode: null,
        title: r.title,
        excerpt: r.description,
        readingTimeMin: r.readingTimeMin,
        publishedAt: r.publishedAt,
      })),
      alternate: alt
        ? { slug: alt.slug, locale: alt.locale, pageType: "editorial", countryCode: null }
        : null,
      faq: staticPost.faq ?? null,
    };
    return data;
  });

// ── Route ───────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await fetchBlogPost({ data: params.slug });
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const post = loaderData as LoaderData;

    const seoPost = {
      locale: post.locale,
      pageType: post.pageType,
      slug: post.slug,
      countryCode: post.countryCode,
    };
    const canonical = getBlogCanonical(seoPost);
    const meta = buildMeta({
      title: (post.metaTitle ?? post.title) + " | Alchema Blog",
      description: post.metaDescription ?? post.excerpt ?? "",
      url: canonical.path,
      type: "article",
      image: post.ogImageUrl ?? undefined,
      locale: post.locale === "de" ? "de_DE" : "en_US",
    });

    meta.push(
      { property: "article:published_time", content: post.publishedAt ?? "" },
    );
    if (post.updatedAt) {
      meta.push({ property: "article:modified_time", content: post.updatedAt });
    }
    for (const tag of post.tags) {
      meta.push({ property: "article:tag", content: tag });
    }

    return { meta, links: getBlogHeadLinks(seoPost, post.alternate) };
  },
  component: BlogDetailPage,
  notFoundComponent: NotFoundPage,
});

// ── Not Found ───────────────────────────────────────────────────────────────

function NotFoundPage() {
  return (
    <div
      className="mx-auto max-w-3xl px-6 py-32 text-center"
      style={{ backgroundColor: "var(--bg)" }}
    >
      <h1
        className="text-2xl font-semibold tracking-tight"
        style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
      >
        Article not found
      </h1>
      <p
        className="mt-3 text-[0.9375rem]"
        style={{ color: "var(--text-secondary)" }}
      >
        This article doesn't exist or has been removed.
      </p>
      <LocalizedBlogLink
        href="/blog"
        className="mt-8 inline-flex rounded-[10px] px-6 py-2.5 text-sm font-medium transition-colors duration-150"
        style={{ background: "var(--accent)", color: "var(--text-inverse)" }}
      >
        View all articles
      </LocalizedBlogLink>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ── Blog Detail Page ────────────────────────────────────────────────────────

function BlogDetailPage() {
  const post = Route.useLoaderData();
  if (!post) return <NotFoundPage />;

  const isStatic = post.source === "static";
  const ContentComponent = isStatic ? CONTENT_MAP[post.slug] ?? null : null;
  const isDE = post.locale === "de";
  const analyticsPageType = asBlogPageType(post.pageType) ?? "editorial";
  const analyticsLocale = asAnalyticsLocale(post.locale) ?? "en";

  useEffect(() => {
    trackEvent("blog_page_view", {
      pageType: analyticsPageType,
      locale: analyticsLocale,
    });
    // Set sessionStorage breadcrumb for signup_from_blog attribution (ANAL-02)
    if (typeof window !== "undefined") {
      try {
        sessionStorage.setItem("blog_referrer", JSON.stringify({
          slug: post.slug,
          pageType: post.pageType,
        }));
      } catch {
        // sessionStorage unavailable
      }
    }
  }, [post.slug, post.pageType, post.pillar?.slug, post.locale, analyticsPageType, analyticsLocale]);

  useBlogEngagement({ slug: post.slug, pageType: analyticsPageType });

  // JSON-LD — always include articleSchema (dynamic dates + Person author) +
  // BreadcrumbList. Merge in any entity-specific schemas from stored schemaJson
  // (FAQPage, Country, City, Occupation, HowTo, CollegeOrUniversity) but skip
  // BlogPosting/BreadcrumbList since we always produce those dynamically.
  const computedSchemas: unknown[] = [
    articleSchema({
      slug: post.slug,
      title: post.title,
      description: post.excerpt ?? "",
      publishedAt: post.publishedAt ?? new Date().toISOString(),
      updatedAt: post.updatedAt ?? undefined,
      author: post.author ?? "Alchema",
      locale: post.locale,
      tags: post.tags,
      readingTimeMin: post.readingTimeMin ?? 5,
      coverImage: post.ogImageUrl ?? undefined,
      pageType: post.pageType,
      countryCode: post.countryCode,
    }),
    breadcrumbSchema([
      { name: "Home", url: post.locale === "en" ? "/" : `/${post.locale}` },
      { name: "Blog", url: getBlogCanonical({ locale: post.locale, pageType: "index" }).path },
      { name: post.title, url: getBlogCanonical({
        locale: post.locale,
        pageType: post.pageType,
        slug: post.slug,
        countryCode: post.countryCode,
      }).path },
    ]),
  ];


  computedSchemas.push(...supplementalBlogSchemaNodes(post.schemaJson));

  // Static-post FAQPage JSON-LD (GEO): emit a FAQPage node when the registry
  // carries faq pairs. Guarded to static source so DB posts (which carry their
  // own schemaJson) are unaffected and never double-emit an FAQPage.
  // schemaGraph strips per-node @context, so no collision with the
  // always-present BlogPosting/BreadcrumbList nodes.
  if (isStatic && post.faq && post.faq.length > 0) {
    computedSchemas.push(faqPageSchema(post.faq));
  }

  const jsonLd = schemaGraph(...computedSchemas);

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={jsonLd} />

      <article className="mx-auto max-w-[680px] px-6 pb-24 pt-24">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="mb-8 text-[0.8125rem]"
          style={{ color: "var(--text-muted)" }}
        >
          <ol className="flex items-center gap-1.5">
            <li>
              <LocalizedBlogLink
                href="/blog"
                locale={post.locale}
                className="transition-colors duration-150 hover:text-[var(--text)]"
              >
                Blog
              </LocalizedBlogLink>
            </li>
            {post.pillar && (
              <>
                <li aria-hidden="true" className="opacity-40">/</li>
                <li>
                  <LocalizedBlogLink
                    href={`/blog/pillar/${post.pillar.slug}`}
                    locale={post.locale}
                    className="transition-colors duration-150 hover:text-[var(--text)]"
                  >
                    {post.pillar.title}
                  </LocalizedBlogLink>
                </li>
              </>
            )}
            <li aria-hidden="true" className="opacity-40">/</li>
            <li
              className="truncate max-w-[240px]"
              style={{ color: "var(--text-secondary)" }}
            >
              {post.title}
            </li>
          </ol>
        </nav>

        {/* Language switcher */}
        {post.alternate && (
          <div className="mb-5">
            <BlogPostLink
              post={post.alternate}
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-150 hover:border-[var(--accent)]"
              style={{
                borderColor: "var(--border)",
                color: "var(--text-secondary)",
              }}
            >
              {isDE ? "Read in English" : "Auf Deutsch lesen"}
            </BlogPostLink>
          </div>
        )}

        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-5">
            {post.tags.map((tag: string) => (
              <span
                key={tag}
                className="rounded-full px-2.5 py-0.5 text-[0.6875rem] font-medium"
                style={{
                  background: "var(--surface-raised)",
                  color: "var(--text-muted)",
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h1
          className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold leading-[1.12] tracking-[-0.025em]"
          style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
        >
          {post.title}
        </h1>

        {/* Author + date + reading time */}
        <div
          className="mt-5 flex items-center gap-2 text-[0.8125rem]"
          style={{ color: "var(--text-muted)" }}
        >
          {post.author && <span>{post.author}</span>}
          {post.author && post.publishedAt && (
            <span aria-hidden="true" className="opacity-40">/</span>
          )}
          {post.publishedAt && (
            <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
          )}
          {post.readingTimeMin && (
            <>
              <span aria-hidden="true" className="opacity-40">/</span>
              <span>{post.readingTimeMin} min read</span>
            </>
          )}
        </div>

        {/* TLDR / Excerpt */}
        {post.excerpt && (
          <div
            className="mt-8 rounded-[12px] p-5"
            style={{ background: "var(--surface-raised)" }}
          >
            <p
              className="text-[0.8125rem] font-medium uppercase tracking-wider mb-2"
              style={{ color: "var(--text-muted)" }}
            >
              TLDR
            </p>
            <p
              className="text-[0.9375rem] leading-relaxed"
              style={{ color: "var(--text-secondary)", maxWidth: "60ch" }}
            >
              {post.excerpt}
            </p>
          </div>
        )}

        {/* Separator */}
        <hr className="my-10" style={{ borderColor: "var(--border-subtle)" }} />

        {/* Content */}
        <div className="blog-prose">
          {ContentComponent ? (
            <Suspense
              fallback={
                <div className="py-12 text-center text-sm" style={{ color: "var(--text-muted)" }}>
                  Loading article...
                </div>
              }
            >
              <ContentComponent />
            </Suspense>
          ) : post.content ? (
            <div
              dangerouslySetInnerHTML={{
                __html: (() => {
                  // Convert markdown → HTML on both server + client so AI crawlers see
                  // semantic structure. DB content is authored by authenticated n8n
                  // callers or our own subagents (trusted); we sanitize on the client
                  // for defense-in-depth without pulling jsdom into the server bundle
                  // (isomorphic-dompurify OOMs the Vercel build container).
                  const html = marked.parse(post.content, { async: false }) as string;
                  return typeof window !== "undefined"
                    ? DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })
                    : html;
                })(),
              }}
            />
          ) : (
            <p style={{ color: "var(--text-secondary)" }}>Content not available.</p>
          )}
        </div>

        {/* Internal links */}
        {post.internalLinks.length > 0 && (
          <div className="mt-12 pt-8" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <h2
              className="text-[0.8125rem] font-medium uppercase tracking-wider mb-4"
              style={{ color: "var(--text-muted)" }}
            >
              Related reading
            </h2>
            <ul className="space-y-2">
              {post.internalLinks.map((link: LoaderData["internalLinks"][number], i: number) => (
                <li key={i}>
                  <BlogPostLink
                    post={{
                      slug: link.toSlug,
                      locale: link.toLocale,
                      pageType: link.toPageType,
                      countryCode: link.toCountryCode,
                    }}
                    className="text-[0.9375rem] font-medium transition-colors duration-150 hover:text-[var(--accent)]"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {link.anchorText ?? link.toTitle}
                  </BlogPostLink>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <div
          className="mt-12 rounded-[12px] p-8"
          style={{ background: "var(--surface-raised)" }}
        >
          <p
            className="text-lg font-semibold leading-tight"
            style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}
          >
            {isDE
              ? "Optimiere deinen Lebenslauf jetzt mit Alchema"
              : "Ready to stand out in the EU job market?"}
          </p>
          <p
            className="mt-2 text-[0.9375rem] leading-relaxed"
            style={{ color: "var(--text-secondary)", maxWidth: "48ch" }}
          >
            {isDE
              ? "KI-gestützte Bewerbungen, made in the EU. ATS-optimiert, EU-Datenverarbeitung (siehe Datenschutzerklärung)."
              : "AI-powered resume tailoring, cover letters, and applications. Built for Europe, EU data processing (see Privacy Policy)."}
          </p>
          <Link
            to="/register"
            onClick={() => trackEvent("blog_cta_click", {
              cta_type: "signup",
              pageType: analyticsPageType,
            })}
            className="mt-5 inline-flex rounded-[10px] px-6 py-2.5 text-sm font-medium transition-colors duration-150"
            style={{ background: "var(--accent)", color: "var(--text-inverse)" }}
          >
            {isDE ? "Kostenlos starten" : "Start for free"}
          </Link>
        </div>

        {/* Related posts */}
        {post.related.length > 0 && (
          <div className="mt-12 pt-8" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <h2
              className="text-[0.8125rem] font-medium uppercase tracking-wider mb-5"
              style={{ color: "var(--text-muted)" }}
            >
              {isDE ? "Weitere Artikel" : "More articles"}
            </h2>
            <div className="space-y-5">
              {post.related.map((r: LoaderData["related"][number]) => (
                <BlogPostLink
                  key={`${r.locale}:${r.slug}`}
                  post={r}
                  className="group block"
                >
                  <h3
                    className="text-[1rem] font-medium leading-snug transition-colors duration-150 group-hover:text-[var(--accent)]"
                    style={{ color: "var(--text)" }}
                  >
                    {r.title}
                  </h3>
                  {r.excerpt && (
                    <p
                      className="mt-1 text-[0.875rem] leading-relaxed"
                      style={{ color: "var(--text-muted)", maxWidth: "55ch" }}
                    >
                      {r.excerpt.length > 120 ? r.excerpt.slice(0, 120) + "..." : r.excerpt}
                    </p>
                  )}
                </BlogPostLink>
              ))}
            </div>
          </div>
        )}

        {/* Back link */}
        <div className="mt-12">
          <LocalizedBlogLink
            href="/blog"
            locale={post.locale}
            className="text-[0.8125rem] font-medium transition-colors duration-150 hover:text-[var(--accent)]"
            style={{ color: "var(--text-muted)" }}
          >
            &larr; All articles
          </LocalizedBlogLink>
        </div>
      </article>
    </div>
  );
}
